import {
  Logo,
  Button,
  Canvas,
  PlusIcon,
  SceneContainer,
  ButtonContainer
} from './Overview.styles';

import Scene from './Scene';
import Content from './content';
import { Theme } from '@/theme';
import API from '@/services/api';
import type { Vector2 } from 'three';
import { connect } from 'react-redux';
import { useLoader } from '@CS/hooks';
import { Images } from '@/assets/images';
import { useDashboardTranslation } from '@/hooks';

import EditButton from '@/components/EditButton';
import AvatarLabel from '@/components/AvatarLabel';
import { useRef, useState, useEffect } from 'react';
import useTranslation from '@CS/hooks/useTranslation';
import type { EntityData } from '@/services/api/types';
import { getCampaign } from '@/redux/campaign/selectors';
import { getUserEntityData } from '@/redux/user/selectors';
import type { CampaignState } from '@/redux/campaign/types';
import { WelcomePopup } from '@/components/Popups/WelcomePopup';
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader';
import { type ISDK, type AvatarData } from '@tbwax_dev/mcd-family-sdk';
import { AvatarCreatePopup } from '@/components/Popups/AvatarCreatePopup';
import {
  FamilyDetailsPopup,
  type FamilyDetails
} from '@/components/Popups/FamilyDetailsPopup';

type OverviewProps = {
  campaign: CampaignState;
  data: EntityData;
  sdk?: ISDK;
};

interface FamilyMember {
  memberData: AvatarData;
  avatar: GLTF;
}

const Overview = ({ sdk, data, campaign }: OverviewProps) => {
  const [hasAvatars, setHasAvatars] = useState<boolean | undefined>(void 0);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [showAvatarCreate, setShowAvatarCreate] = useState(false);
  const [showFamilyDetails, setShowFamilyDetails] = useState(false);
  const [selectedFamilyMembers, setSelectedFamilyMembers] = useState(1);
  const [familyDetails, setFamilyDetails] = useState<FamilyDetails>({
    adults: 0,
    children: 0,
    childBirthDates: []
  });

  const [showWelcome, setShowWelcome] = useState(false);
  const [labels, setLabels] = useState<Vector2[]>([]);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [loading, setLoading] = useState(true);

  const buttonFallback = useTranslation(Content);
  const t = useDashboardTranslation();
  const button = t('Family_onboarding_avatar_title', buttonFallback);
  const names = useRef<string[]>([]);
  const scene = useRef<Scene>(null);

  const loader = useLoader(loading, { color: Theme.color.orange });

  const createAvatar = () => {
    const params = new URLSearchParams();
    params.set('members', String(selectedFamilyMembers));
    location.href = `${window.location.origin}/avatar-creator/?${params.toString()}`;
  };

  const handleClosePopup = () => {
    if (showWelcome) {
      setShowWelcome(false);
      setShowFamilyDetails(true);
    } else if (showFamilyDetails) {
      const familyMembersTotal = familyDetails.adults + familyDetails.children;
      const nextSelectedFamilyMembers =
        familyMembersTotal || selectedFamilyMembers;

      setSelectedFamilyMembers(nextSelectedFamilyMembers);
      setShowFamilyDetails(false);
      setShowAvatarCreate(true);

      API.updateUser({
        data: {
          ...data,
          familyMembers: nextSelectedFamilyMembers,
          familyProfile: {
            ...familyDetails,
            childBirthDates: familyDetails.childBirthDates.filter(Boolean),
            completedAt: new Date().toISOString()
          }
        }
      });
    } else if (showAvatarCreate) {
      setShowAvatarCreate(false);
      API.updateUser({
        data: {
          ...data,
          popups: true,
          familyMembers: selectedFamilyMembers
        }
      });
    }
  };

  const loadFamilyData = async (sdk: ISDK) => {
    try {
      const familyData = await sdk.getFamilyData();
      const { length } = familyData?.avatars ?? {};

      if (!length) {
        setHasAvatars(false);
        setLoading(false);
        return;
      }

      const member = familyData?.avatars;
      const avatars = await sdk.getFamilyAvatars();
      if (!avatars) return console.error('Avatars not loaded.');

      const membersData = avatars
        .map((avatar, a) => ({ avatar, memberData: member?.[a] }))
        .filter((item): item is FamilyMember => !!item.memberData);

      setFamilyMembers(membersData);
      setHasAvatars(true);
      setLoading(false);
    } catch (error) {
      setHasAvatars(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    const { length } = familyMembers;
    if (!length || !hasAvatars || !canvas.current || labels.length) return;

    scene.current = new Scene(canvas.current);

    familyMembers.forEach(({ avatar, memberData: { age, name } }, m) => {
      scene.current!.addAvatar(avatar, age, m, length);
      names.current.push(name);
    });

    setLabels(scene.current.labels);
  }, [canvas.current, familyMembers, hasAvatars]);

  useEffect(() => {
    if (!data) return;
    const { popups = false } = data ?? {};
    const members = Number(data.familyMembers);

    if (Number.isInteger(members) && members >= 1 && members <= 8) {
      setSelectedFamilyMembers(members);
    }

    if (
      hasAvatars === false &&
      !popups &&
      !showWelcome &&
      !showFamilyDetails &&
      !showAvatarCreate
    ) {
      setShowAvatarCreate(false);
      setShowFamilyDetails(false);
      setShowWelcome(true);
    }
    //
    else if (popups || hasAvatars !== false) {
      setShowAvatarCreate(false);
      setShowFamilyDetails(false);
      setShowWelcome(false);
    }
  }, [data, hasAvatars]);

  useEffect(() => {
    sdk && loadFamilyData(sdk);
  }, [sdk]);

  if (loading || !data)
    return (
      <SceneContainer>
        <Logo alt="Family Mode" src={Images.Logo} />
        {loader}
      </SceneContainer>
    );

  if (!hasAvatars) {
    return (
      <>
        <SceneContainer>
          <Logo alt="Family Mode" src={Images.Logo} />

          <ButtonContainer>
            <Button onClick={createAvatar}>
              <PlusIcon src={Images.PlusIcon} alt="Plus Icon" />
              {button}
            </Button>
          </ButtonContainer>
        </SceneContainer>

        {!(data?.popups ?? false) && (
          <>
            {showWelcome && <WelcomePopup onClose={handleClosePopup} />}
            {showFamilyDetails && (
              <FamilyDetailsPopup
                value={familyDetails}
                onChange={setFamilyDetails}
                onClose={handleClosePopup}
              />
            )}
            {showAvatarCreate && (
              <AvatarCreatePopup onClose={handleClosePopup} />
            )}
          </>
        )}
      </>
    );
  }

  return (
    <SceneContainer>
      <Logo alt="Family Mode" src={Images.Logo} />
      <Canvas ref={canvas} />

      {labels.map(({ x, y }, n) => (
        <AvatarLabel name={names.current[n]} left={x} top={y} key={n} />
      ))}

      <EditButton onClick={createAvatar} />
    </SceneContainer>
  );
};

const mapStateToProps = (state: never) => ({
  data: getUserEntityData(state),
  campaign: getCampaign(state)
});

export default connect(mapStateToProps)(Overview);
